<template>
  <section
    :style="
      data.update_background
        ? { backgroundColor: data.section_background }
        : null
    "
    class="brands-module-component"
    :class="{ 'no-padding': data.remove_padding_bottom }">
    <template v-if="data.add_top_spacer">
      <SpacerComponent :data="negativeSpacerData" />
      <SpacerComponent :data="positiveSpacerData" />
    </template>

    <img
      v-if="data.show_brands"
      class="background-logo"
      src="@/assets/img/logo-outline.svg"
      alt="Logo" />

    <div class="inner-container">
      <div
        class="top-text"
        :class="{
          full: data.full_width,
        }"
        v-html="props.data.top_text"></div>

      <div
        v-if="data.show_brands"
        class="content">
        <div
          v-for="(brand, index) in props.data.brands"
          :key="index"
          class="brands">
          <img
            :src="brand.brand_logo.url"
            :alt="brand.brand_logo.alt" />

          <div class="divider"></div>

          <div class="buttons">
            <div
              v-for="(button, btnIndex) in brand.buttons"
              :key="btnIndex"
              class="with-button">
              <div
                v-if="button.description"
                class="description"
                v-html="button.description"></div>
              <div class="buttons-container">
                <a
                  v-for="(inner_button, btnIndex) in button.add_buttons"
                  :key="btnIndex"
                  class="button"
                  :class="{
                    green: inner_button.green_button,
                  }"
                  :href="inner_button.button.url"
                  :target="inner_button.button.target"
                  v-html="inner_button.button.title"></a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "BrandsComponent",
  "CLASSNAME": "brands-module-component",
  "URL": "http://vuevitewp.local/test"
 }
#ENDMETA */

/*
 * Insert your logic here
 */

import { computed } from 'vue'
import SpacerComponent from '@/components/modules/SpacerComponent.vue'

const props = defineProps(['data'])

const spacerSettings = computed(() => props.data?.spacer || {})

const spacerValue = (snakeCaseKey, camelCaseKey) => {
  const value =
    spacerSettings.value?.[snakeCaseKey] ?? spacerSettings.value?.[camelCaseKey]
  const number = Number.parseFloat(value)

  return Number.isFinite(number) ? Math.abs(number) : 0
}

const spacerHeights = computed(() => ({
  desktop: spacerValue('desktop_spacer', 'desktopSpacer'),
  tablet: spacerValue('tablet_spacer', 'tabletSpacer'),
  mobile: spacerValue('mobile_spacer', 'mobileSpacer'),
}))

const negativeSpacerData = computed(() => ({
  separator: false,
  rounded: 'none',
  spacer_height: -spacerHeights.value.desktop,
  spacer_height_tablet: -spacerHeights.value.tablet,
  spacer_height_mobile: -spacerHeights.value.mobile,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color: 'rgba(255, 255, 255, 0)',
}))

const positiveSpacerData = computed(() => ({
  separator: false,
  rounded:
    spacerSettings.value?.rounded_corner ??
    spacerSettings.value?.roundedCorner ??
    'none',
  spacer_height: spacerHeights.value.desktop,
  spacer_height_tablet: spacerHeights.value.tablet,
  spacer_height_mobile: spacerHeights.value.mobile,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color:
    props.data.spacer?.background_color ??
    props.data.spacer?.backgroundColor ??
    'rgba(255, 255, 255, 1)',
}))
</script>

<style lang="scss" scoped>
.brands-module-component {
  @apply w-full relative pb-8 lg:pb-[65px];

  &.no-padding {
    @apply pb-1;
  }

  .background-logo {
    @apply absolute top-0 right-0 max-w-none w-full h-full object-cover z-0;
    @apply lg:left-[calc(50%-280px)];
  }

  .top-text {
    @apply mb-2 lg:max-w-[560px] lg:ml-0;

    &.full {
      @apply lg:max-w-full;
    }

    &:deep(*) {
      @apply header-4 text-humble text-center mb-8;
      @apply lg:mb-10 lg:text-left;
    }
  }

  .content {
    @apply flex flex-col gap-10;

    .brands {
      @apply bg-white rounded-[20px] p-5 flex flex-col lg:flex-row lg:items-stretch;
      @apply lg:py-[40px] lg:px-[30px] lg:justify-between;

      .divider {
        @apply w-full h-[1px] bg-functional my-[30px] lg:my-0 lg:mx-[50px] lg:h-auto lg:w-[1px];
        @apply lg:flex-[1_0_auto] lg:max-w-[1px];
      }

      img {
        @apply w-full max-w-fit object-contain mx-auto lg:mx-0 lg:max-w-[560px];
      }

      .buttons {
        @apply lg:flex-1 flex flex-col gap-5;

        .with-button {
          @apply p-5 bg-functional rounded-[20px];
        }

        .description {
          @apply mb-3;

          &:deep(*) {
            @apply text-[18px] text-center leading-[22px] mb-3 text-balanced;
          }
        }

        .buttons-container {
          @apply flex flex-col gap-[10px];

          :deep(.button) {
            @apply transition-all pt-[6px] pb-[4px] px-4 flex bg-progressive justify-center rounded-[29px] items-center gap-5;
            @apply text-[18px] text-center font-medium leading-[22px] text-humble;

            &:hover {
              @apply bg-humble text-progressive;
            }
          }

          &:deep(.button.green) {
            @apply bg-humble text-progressive;

            &:hover {
              @apply bg-progressive text-humble;
            }
          }
        }
      }
    }
  }
}
</style>
